import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';

import {
  API_CONFIG,
  AppRoutes,
  EXCLUDED_ALERT_ERROR_ENDPOINTS,
  EXCLUDED_ALERT_STATUS_CODES,
  ERROR_UNEXPECTED,
} from '@/constants';
import { navigate } from '@/providers';
import { convertDatesToISO, showAlertError } from '@/utils';

const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: convert all api requests to snake_case
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config };

  // Skip conversion for multipart/form-data
  if (newConfig.headers?.['Content-Type'] === 'multipart/form-data') {
    return newConfig;
  }

  // Convert params to snake_case
  if (config.params) {
    newConfig.params = decamelizeKeys(convertDatesToISO(config.params, 'YYYY-MM-DDTHH:mm:ssZ'));
  }

  // Convert request data to snake_case
  if (config.data) {
    newConfig.data = decamelizeKeys(convertDatesToISO(config.data, 'YYYY-MM-DDTHH:mm:ssZ'));
  }

  return newConfig;
});

// Response interceptor: handle errors globally
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Convert response data to camelCase
    if (response.data && String(response.headers['content-type']).includes('application/json')) {
      response.data = camelizeKeys(response.data);
    }
    return response;
  },
  async (error: AxiosError) => {
    // Check if the request is for auth OTP endpoints (exclude from global error handling)
    const shouldSkipErrorAlert = (url?: string, status?: number) => {
      if (!url || !status) return false;
      if (!EXCLUDED_ALERT_STATUS_CODES.includes(status)) return false;
      const patterns = EXCLUDED_ALERT_ERROR_ENDPOINTS.map((endpoint) =>
        endpoint.replace(/:[^/]+/g, '[^/]+'),
      );
      return patterns.some((pattern) => new RegExp(pattern).test(url));
    };

    if (!shouldSkipErrorAlert(error.config?.url, error.response?.status)) {
      // If unauthorized, redirect to login page
      if (error.response?.status === 401) {
        navigate(AppRoutes.LOGIN_PAGE, { replace: true });
        return Promise.reject(error);
      }
    }

    // Show error alert for other errors (except auth OTP endpoints)
    const response = camelizeKeys(error.response?.data) as { errorMessage?: string };
    const errorMessage = response?.errorMessage || error.message || ERROR_UNEXPECTED;
    if (!shouldSkipErrorAlert(error.config?.url, error.response?.status)) {
      showAlertError(errorMessage);
    }

    if (error.response) {
      error.response.data = camelizeKeys(error.response.data);
    }

    if (error.response?.status === 403) {
      navigate(AppRoutes.FORBIDDEN, { replace: true });
    }

    return Promise.reject(error);
  },
);

export default api;
